import React, { useEffect, useMemo, useState } from "react";
import { GoogleMap, Polygon } from "@react-google-maps/api";
import Goback from "../../Components/goBack";
import WithGoogleMapsLoader from "../../Components/WithGoogleMapsLoader";
import { ProtectedRoutes } from "../../config/protectedRoutes";
import { useGetAllZonesQuery } from "./ZoneApi";
import ZonesLegendSidebar from "./components/ZonesLegendSidebar";
import { zoneAreaPolygon } from "./utils/geo";
import { colorForIndex } from "./utils/colors";
import { CALABAR_CENTER } from "./components/ZoneMapCanvas";

const ZonesMap = ({ zones, visibleZoneIds, emphasizedZoneId }) => {
  const [map, setMap] = useState(null);

  // Polygons added as soon as `zones` first resolves land right as the map
  // is still settling its panes/projection, so they're silently not painted
  // (a known @react-google-maps/api timing quirk) even though they're
  // correctly attached with valid paths. `map` and `zones` can each become
  // ready before the other, so this effect depends on both and re-checks
  // every time either changes. A tiny pan nudge forces Maps to redraw its
  // overlay panes (more reliable for this than a resize trigger alone).
  useEffect(() => {
    if (map && zones.length) {
      window.google.maps.event.trigger(map, "resize");
      map.setCenter(CALABAR_CENTER);
      map.panBy(1, 0);
      map.panBy(-1, 0);
    }
  }, [map, zones, visibleZoneIds]);

  return (
    <GoogleMap
      mapContainerClassName='w-full h-full'
      center={CALABAR_CENTER}
      zoom={12}
      onLoad={(loadedMap) => {
        setMap(loadedMap);
        setTimeout(() => {
          window.google.maps.event.trigger(loadedMap, "resize");
          loadedMap.setCenter(CALABAR_CENTER);
        }, 0);
      }}
      options={{ streetViewControl: false, mapTypeControl: false, clickableIcons: false }}
    >
      {zones.map((zone, zoneIndex) => {
        if (!visibleZoneIds.has(zone.id)) return null;

        const path = zoneAreaPolygon(zone.areas);
        if (!path.length) return null;

        const zoneColor = colorForIndex(zoneIndex);
        const dimmed = emphasizedZoneId && emphasizedZoneId !== zone.id;

        return (
          <Polygon
            key={zone.id}
            paths={path}
            options={{
              fillColor: zoneColor,
              strokeColor: zoneColor,
              fillOpacity: dimmed ? 0.08 : 0.35,
              strokeOpacity: dimmed ? 0.35 : 0.9,
              strokeWeight: emphasizedZoneId === zone.id ? 3 : 1.5,
              clickable: false,
            }}
          />
        );
      })}
    </GoogleMap>
  );
};

const WrappedZonesMap = WithGoogleMapsLoader(ZonesMap);

const ZonesOverviewMap = () => {
  const { data, isLoading } = useGetAllZonesQuery({ page: 1, limit: 100 });
  const zones = useMemo(() => data?.data || [], [data]);

  const [visibleZoneIds, setVisibleZoneIds] = useState(new Set());
  const [emphasizedZoneId, setEmphasizedZoneId] = useState(null);

  useEffect(() => {
    if (zones.length) setVisibleZoneIds(new Set(zones.map((z) => z.id)));
  }, [zones]);

  const toggleVisible = (zoneId) => {
    setVisibleZoneIds((prev) => {
      const next = new Set(prev);
      if (next.has(zoneId)) next.delete(zoneId);
      else next.add(zoneId);
      return next;
    });
  };

  return (
    <div className='flex flex-col h-[calc(100vh-64px)]'>
      <div className='px-6 pt-4'>
        <Goback currentRoute='All Zones Map' />
      </div>

      {isLoading ? (
        <p className='px-6 text-sm text-primaryAsh'>Loading zones…</p>
      ) : (
        <div className='flex flex-1 min-h-0'>
          <ZonesLegendSidebar
            zones={zones}
            visibleZoneIds={visibleZoneIds}
            onToggleVisible={toggleVisible}
            emphasizedZoneId={emphasizedZoneId}
            onEmphasize={setEmphasizedZoneId}
          />
          <div className='flex-1 min-w-0'>
            <WrappedZonesMap
              zones={zones}
              visibleZoneIds={visibleZoneIds}
              emphasizedZoneId={emphasizedZoneId}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ProtectedRoutes(ZonesOverviewMap, [1, 2]);
